import {
  FleetAreaCode,
  PermissionCode,
  ProductCode,
  type FleetSwitchableArea,
} from '@gb-ticket/shared-types';
import {
  AlertTriangleIcon,
  CalendarCheckIcon,
  CarFrontIcon,
  GaugeIcon,
  IdCardIcon,
  LayoutDashboardIcon,
  ListTodoIcon,
  MapIcon,
  PlugZapIcon,
  RouteIcon,
} from 'lucide-react';

import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { ProductSideNav, type ProductSideNavItem } from '@/platform/components/product-side-nav';
import { registerProductNav } from '@/platform/lib/product-nav';
import {
  cachedFleetAreaOn,
  fleetAreaOn,
  useFleetAreas,
} from '@/products/fleet/features/use-fleet-areas';
import {
  FLEET_BOOK_PATH,
  FLEET_DRIVERS_PATH,
  FLEET_ENERGY_PATH,
  FLEET_ODOMETER_PATH,
  FLEET_POLICY_PATH,
  FLEET_REPORT_PATH,
  FLEET_ROOT,
  FLEET_TASKS_PATH,
  FLEET_TRIPS_PATH,
  FLEET_OPERATIONS_PATH,
  FLEET_VEHICLES_PATH,
} from '@/products/fleet/fleet-paths';

const iconClass = 'size-4';

function FleetNav() {
  const me = useMe();
  const areas = useFleetAreas();
  const catalog = areas.isError ? undefined : areas.data;
  const area = (code: FleetSwitchableArea) => fleetAreaOn(catalog, code);
  const manage = hasPermission(me.data, PermissionCode.FLEET_VEHICLES_MANAGE);
  const compliance = hasPermission(me.data, PermissionCode.FLEET_COMPLIANCE_MANAGE);
  const pool = hasPermission(me.data, PermissionCode.FLEET_POOL_BOOK) && area(FleetAreaCode.POOL);
  const mileage =
    hasPermission(me.data, PermissionCode.FLEET_MILEAGE_WRITE) && area(FleetAreaCode.MILEAGE);
  const damage =
    hasPermission(me.data, PermissionCode.FLEET_DAMAGE_CREATE) && area(FleetAreaCode.DAMAGE);
  const policy = hasPermission(me.data, PermissionCode.FLEET_POLICY_MANAGE);
  const energy =
    hasPermission(me.data, PermissionCode.FLEET_ENERGY_MANAGE) && area(FleetAreaCode.TELEMATICS);
  const telematicsRead = hasPermission(me.data, PermissionCode.FLEET_TELEMATICS_READ);
  const fuelRead = hasPermission(me.data, PermissionCode.FLEET_FUEL_READ);
  const tripsOwn =
    hasPermission(me.data, PermissionCode.FLEET_TRIPS_READ_OWN) && area(FleetAreaCode.TRIPS);
  const drivers = compliance && (area(FleetAreaCode.COMPLIANCE) || area(FleetAreaCode.INSTRUCTION));
  const betrieb =
    (telematicsRead && area(FleetAreaCode.TELEMATICS)) || (fuelRead && area(FleetAreaCode.FUEL));

  const items: ProductSideNavItem[] = [
    {
      to: FLEET_ROOT,
      label: 'Übersicht',
      end: true,
      icon: <LayoutDashboardIcon aria-hidden className={iconClass} strokeWidth={1.75} />,
    },
    ...(pool
      ? [
          {
            to: FLEET_BOOK_PATH,
            label: 'Buchen',
            icon: <CalendarCheckIcon aria-hidden className={iconClass} strokeWidth={1.75} />,
          },
        ]
      : []),
    ...(mileage
      ? [
          {
            to: FLEET_ODOMETER_PATH,
            label: 'Kilometer',
            icon: <GaugeIcon aria-hidden className={iconClass} strokeWidth={1.75} />,
          },
        ]
      : []),
    ...(damage
      ? [
          {
            to: FLEET_REPORT_PATH,
            label: 'Melden',
            icon: <AlertTriangleIcon aria-hidden className={iconClass} strokeWidth={1.75} />,
          },
        ]
      : []),
    ...(tripsOwn
      ? [
          {
            to: FLEET_TRIPS_PATH,
            label: 'Fahrten',
            icon: <RouteIcon aria-hidden className={iconClass} strokeWidth={1.75} />,
          },
        ]
      : []),
    ...(manage
      ? [
          {
            to: FLEET_VEHICLES_PATH,
            label: 'Fahrzeuge',
            icon: <CarFrontIcon aria-hidden className={iconClass} strokeWidth={1.75} />,
          },
        ]
      : []),
    ...(manage
      ? [
          {
            to: FLEET_TASKS_PATH,
            label: 'Aufgaben',
            icon: <ListTodoIcon aria-hidden className={iconClass} strokeWidth={1.75} />,
          },
        ]
      : []),
    ...(drivers
      ? [
          {
            to: FLEET_DRIVERS_PATH,
            label: 'Fahrer',
            icon: <IdCardIcon aria-hidden className={iconClass} strokeWidth={1.75} />,
          },
        ]
      : []),
    ...(betrieb
      ? [
          {
            to: FLEET_OPERATIONS_PATH,
            label: 'Betrieb',
            icon: <MapIcon aria-hidden className={iconClass} strokeWidth={1.75} />,
          },
        ]
      : []),
    ...(energy
      ? [
          {
            to: FLEET_ENERGY_PATH,
            label: 'E-Management',
            icon: <PlugZapIcon aria-hidden className={iconClass} strokeWidth={1.75} />,
          },
        ]
      : []),
    ...(policy ? [{ to: FLEET_POLICY_PATH, label: 'Verwaltung' }] : []),
  ];

  return <ProductSideNav ariaLabel="Fuhrparkmanagement" items={items} />;
}

registerProductNav({
  code: ProductCode.FLEET,
  Nav: FleetNav,
  menuTiles: [
    { id: 'fleet-home', label: 'Übersicht', to: FLEET_ROOT },
    {
      id: 'fleet-book',
      label: 'Buchen',
      to: FLEET_BOOK_PATH,
      isVisible: (user) =>
        hasPermission(user, PermissionCode.FLEET_POOL_BOOK) &&
        cachedFleetAreaOn(FleetAreaCode.POOL),
    },
    {
      id: 'fleet-odometer',
      label: 'Kilometer',
      to: FLEET_ODOMETER_PATH,
      isVisible: (user) => hasPermission(user, PermissionCode.FLEET_MILEAGE_WRITE),
    },
    {
      id: 'fleet-report',
      label: 'Melden',
      to: FLEET_REPORT_PATH,
      isVisible: (user) => hasPermission(user, PermissionCode.FLEET_DAMAGE_CREATE),
    },
    {
      id: 'fleet-trips',
      label: 'Fahrten',
      to: FLEET_TRIPS_PATH,
      isVisible: (user) => hasPermission(user, PermissionCode.FLEET_TRIPS_READ_OWN),
    },
    {
      id: 'fleet-vehicles',
      label: 'Fahrzeuge',
      to: FLEET_VEHICLES_PATH,
      isVisible: (user) => hasPermission(user, PermissionCode.FLEET_VEHICLES_MANAGE),
    },
    {
      id: 'fleet-tasks',
      label: 'Aufgaben',
      to: FLEET_TASKS_PATH,
      isVisible: (user) => hasPermission(user, PermissionCode.FLEET_VEHICLES_MANAGE),
    },
    {
      id: 'fleet-drivers',
      label: 'Fahrer',
      to: FLEET_DRIVERS_PATH,
      isVisible: (user) => hasPermission(user, PermissionCode.FLEET_COMPLIANCE_MANAGE),
    },
    {
      id: 'fleet-operations',
      label: 'Betrieb',
      to: FLEET_OPERATIONS_PATH,
      isVisible: (user) =>
        hasPermission(user, PermissionCode.FLEET_TELEMATICS_READ) ||
        hasPermission(user, PermissionCode.FLEET_FUEL_READ),
    },
    {
      id: 'fleet-energy',
      label: 'E-Management',
      to: FLEET_ENERGY_PATH,
      isVisible: (user) =>
        hasPermission(user, PermissionCode.FLEET_ENERGY_MANAGE) &&
        cachedFleetAreaOn(FleetAreaCode.TELEMATICS),
    },
    {
      id: 'fleet-policy',
      label: 'Verwaltung',
      to: FLEET_POLICY_PATH,
      isVisible: (user) => hasPermission(user, PermissionCode.FLEET_POLICY_MANAGE),
    },
  ],
  mobileSlots: [
    {
      kind: 'link',
      id: 'fleet-vehicles',
      label: 'Fahrzeuge',
      to: FLEET_VEHICLES_PATH,
      isVisible: (user) => hasPermission(user, PermissionCode.FLEET_VEHICLES_MANAGE),
    },
    {
      kind: 'link',
      id: 'fleet-book',
      label: 'Buchen',
      to: FLEET_BOOK_PATH,
      isVisible: (user) =>
        hasPermission(user, PermissionCode.FLEET_POOL_BOOK) &&
        cachedFleetAreaOn(FleetAreaCode.POOL),
    },
    { kind: 'link', id: 'fleet-home', label: 'Übersicht', to: FLEET_ROOT },
  ],
});
